
import { isEmail } from 'bits/email'
import window from 'bits/global'
import { transformUrl, buildUrlFromPath, goBack, reload } from 'bits/location'
import { addQueryParams } from 'bits/url'
import { Action, Component, Getter, State, Vue, Watch, namespace } from 'bits/vue'
import OzBackButton from 'library/v4/components/OzBackButton.vue'
import OzCheckBox from 'library/v4/components/OzCheckBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from 'library/v4/components/OzContainedButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzInput from 'library/v4/components/OzInput.vue'
import OzLabelInput from 'library/v4/components/OzLabelInput.vue'
import OzLoadingText from 'library/v4/components/OzLoadingText.vue'
import OzPadletLogo from 'library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from 'library/v4/components/OzPlainButton.vue'
import OzTextLink from 'library/v4/components/OzTextLink.vue'
import AuthActionMenu from 'vuecomponents/AuthActionMenu.vue'
import AuthBottomNav from 'vuecomponents/AuthBottomNav.vue'
import AuthLoginThirdPartyGroupButtons, {
  AuthLoginThirdPartyGroupButtonsType,
} from 'vuecomponents/AuthLoginThirdPartyGroupButtons.vue'
import GlobalSnackbar from 'vuecomponents/GlobalSnackbarVuex.vue'
import VisualViewportSizeMixin from 'vuemixins/visual_viewport_size_mixin'
import WindowSizeMixin from 'vuemixins/window_size_mixin'
import type { LoginMethod } from 'vuexstore/landside'

const browserStore = namespace('browser')

@Component({
  components: {
    OzIcon,
    OzContainedButton,
    OzBackButton,
    OzLabelInput,
    OzInput,
    OzTextLink,
    OzCheckBox,
    OzLoadingText,
    OzPadletLogo,
    OzPlainButton,
    AuthLoginThirdPartyGroupButtons,
    AuthActionMenu,
    AuthBottomNav,
    GlobalSnackbar,
  },
  mixins: [WindowSizeMixin, VisualViewportSizeMixin],
})
export default class AuthLoginApp extends Vue {
  $refs!: {
    usernameInput: OzInput
    passwordInput: OzInput
  }

  OzPlainButtonColorScheme = OzPlainButtonColorScheme
  OzPlainButtonSizePreset = OzPlainButtonSizePreset
  OzContainedButtonColorScheme = OzContainedButtonColorScheme
  AuthLoginThirdPartyGroupButtonsType = AuthLoginThirdPartyGroupButtonsType

  xActionMenu = false
  isUsernameInputFocused = false
  isPasswordInputFocused = false
  isPasswordShown = false
  initialScrollY = 0
  prefilledPasswordByBrowser = ''
  reloadPage = reload

  @State username!: string
  @State password!: string
  @State usernameValidationMessage!: string
  @State passwordValidationMessage!: string
  @State dir!: string
  @State isElectronApp!: boolean
  @State isAwaitingServerResponse!: boolean
  // State variables for PKCE verification
  @State codeChallenge!: string | null
  @State codeChallengeMethod!: string | null
  @State pkceState!: string | null
  @State originAppClientId!: string | null
  @State originAppRedirectUri!: string | null
  @Getter isPkceEnabled!: boolean
  @Getter thirdPartyAuthLoginMethods!: Record<string, LoginMethod>
  // Switch to the login with password UI when isUsernameVerified is true
  @State isUsernameVerified!: boolean
  @Getter isUsernameValid!: boolean
  @Getter isPasswordValid!: boolean
  @Getter userThirdPartyAuthLoginMethods!: Record<string, LoginMethod>
  @Action setUsername!: (username: string) => void
  @Action setPassword!: (password: string) => void
  @Action setUsernameValidationMessage!: (message: string) => void
  @Action setPasswordValidationMessage!: (message: string) => void
  @Action setIsUsernameVerified!: (value: boolean) => void
  @Action continueWithEmailOrUsername!: () => Promise<void>
  @Action loginWithPassword!: () => void
  @browserStore.Getter windowWidth!: number
  @browserStore.Getter visualViewportHeight!: number
  @browserStore.Getter isSmallerThanTabletPortrait!: boolean
  @browserStore.Getter isMobile!: boolean
  @browserStore.Getter isTablet!: boolean
  @browserStore.Getter isDesktop!: boolean
  @browserStore.Getter isAndroid!: boolean

  // Header elements are hidden when the input is focused on android phones.
  get xHeader(): boolean {
    return !(
      this.isMobile &&
      this.isAndroid &&
      this.isSmallerThanTabletPortrait &&
      (this.isUsernameInputFocused || this.isPasswordInputFocused)
    )
  }

  get isContinueButtonDisabled(): boolean {
    return !this.username.length || !!this.usernameValidationMessage.length
  }

  get isLoginButtonDisabled(): boolean {
    return !this.password.length || !!this.passwordValidationMessage.length
  }

  get signUpPageUrl(): string {
    const baseSignupUrl = buildUrlFromPath('/auth/signup')
    if (this.isPkceEnabled) {
      const signupUrlWithPkceParams = transformUrl(baseSignupUrl, {
        searchParams: {
          code_challenge: this.codeChallenge as string,
          code_challenge_method: this.codeChallengeMethod as string,
          redirect_uri: encodeURIComponent(this.originAppRedirectUri as string),
          client_id: this.originAppClientId as string,
          pkce_state: this.pkceState as string,
        },
      })
      return signupUrlWithPkceParams
    } else {
      return baseSignupUrl
    }
  }

  get orgDomainVerificationPageUrl(): string {
    const baseOrgDomainVerificationPageUrl = buildUrlFromPath('/auth/org_login_redirect')
    if (this.isPkceEnabled) {
      const orgDomainVerificationPageUrlWithPkceParams = transformUrl(baseOrgDomainVerificationPageUrl, {
        searchParams: {
          code_challenge: this.codeChallenge as string,
          code_challenge_method: this.codeChallengeMethod as string,
          redirect_uri: encodeURIComponent(this.originAppRedirectUri as string),
          client_id: this.originAppClientId as string,
          pkce_state: this.pkceState as string,
        },
      })
      return orgDomainVerificationPageUrlWithPkceParams
    } else {
      return baseOrgDomainVerificationPageUrl
    }
  }

  get passwordScreenHeading(): string {
    return isEmail(this.username) ? this.__('Log in with email') : this.__('Log in with username')
  }

  get forgotPasswordUrl(): string {
    return buildUrlFromPath('/auth/forgot')
  }

  get xUsernameVerificationScreen(): boolean {
    return !this.isUsernameVerified
  }

  @Watch('visualViewportHeight')
  respondToVisualViewportHeightChange(newValue: number, oldValue: number): void {
    if (this.isDesktop || newValue >= oldValue) return

    this.initialScrollY = window.scrollY

    // When people focus on the input, the keyboard will pop up
    // so the visual viewport height will be narrowed down.
    // we want to hide the keyboard when people try to scroll the page
    if (this.isUsernameInputFocused) {
      document.addEventListener('scroll', this.blurUsernameInputOnScroll)
    } else if (this.isPasswordInputFocused) {
      document.addEventListener('scroll', this.blurPasswordInputOnScroll)
    }
  }

  @Watch('xUsernameVerificationScreen', { immediate: true })
  respondToScreenChange(newValue: boolean): void {
    // Only focus on current screen input field after a 1ms `setTimeout`,
    // because we need to wait for the input field to be shown in the DOM,
    // before we can focus on it.
    setTimeout(() => {
      this.$nextTick(() => {
        if (newValue) {
          this.$refs.usernameInput.focusInput()
        } else {
          this.$refs.passwordInput.focusInput()
        }
      })
    }, 1)
  }

  @Watch('isAwaitingServerResponse')
  handleIsAwaitingServerResponse(value: boolean): void {
    const html = document.getElementsByTagName('html')[0]

    if (this.xUsernameVerificationScreen) return

    // Animate the background color while waiting for the response
    // Note: Only do this when submitting the login form
    if (value) {
      html.classList.add('dot-grid-loading')
    } else {
      html.classList.remove('dot-grid-loading')
    }
  }

  beforeDestroy(): void {
    window.removeEventListener('popstate', this.goBackEmailVerificationScreen)
  }

  onInputUsernameInput(e): void {
    // Prevent the input from being refilled by browser after clicking the Continue button
    if (!this.xUsernameVerificationScreen) return

    this.setUsername(e.target.value)

    // Reset the username validation message to enable the Continue button when typing on mobile and tablet
    if (this.usernameValidationMessage.length) this.setUsernameValidationMessage('')
  }

  onInputPasswordInput(e): void {
    this.setPassword(this.prefilledPasswordByBrowser || e.target.value)

    // Prevent the value of password input from always being prefilledPasswordByBrowser
    if (this.prefilledPasswordByBrowser) {
      this.prefilledPasswordByBrowser = ''
    }

    // Reset the password validation message to enable the Login button when typing on mobile and tablet
    if (this.passwordValidationMessage.length) this.setPasswordValidationMessage('')
  }

  onFocusInUsernameInput(): void {
    this.isUsernameInputFocused = true
  }

  onFocusInPasswordInput(): void {
    this.isPasswordInputFocused = true
  }

  onBlurUsernameInput(): void {
    this.isUsernameInputFocused = false
    document.removeEventListener('scroll', this.blurUsernameInputOnScroll)
  }

  onBlurPasswordInput(): void {
    this.isPasswordInputFocused = false
    document.removeEventListener('scroll', this.blurPasswordInputOnScroll)
  }

  blurUsernameInputOnScroll(): void {
    // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
    // we just blur the input only if the user scrolls past a certain threshold
    if (this.isExceededScrollableThreshold(window.scrollY)) {
      this.$refs.usernameInput.blurInput()
    }
  }

  blurPasswordInputOnScroll(): void {
    // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
    // we just blur the input only if the user scrolls past a certain threshold
    if (this.isExceededScrollableThreshold(window.scrollY)) {
      this.$refs.passwordInput.blurInput()
    }
  }

  isExceededScrollableThreshold(currentWindowY: number): boolean {
    const SCROLLABLE_THRESHOLD = 40 // the height of the input field
    return (
      currentWindowY > this.initialScrollY + SCROLLABLE_THRESHOLD ||
      currentWindowY < this.initialScrollY - SCROLLABLE_THRESHOLD
    )
  }

  goBackEmailVerificationScreen(event: PopStateEvent | Event): void {
    // Go back to the email verification screen when clicking the back button on the password page
    // Assuming the login flow is:
    // (1) Visit home page → (2) Click Log in button → (3) Navigate to login page → (4) Enter email and click the Continue button
    // → (5) Navigate to the password page → (6) Click the back button on the page → (7) Navigate back to the email page → (8) Click the browser back button.
    // If we don't call `goBack()`, we have to do the step (8) twice to go back to the home page at step (1).
    if (event.type === 'click') {
      goBack()
      return
    }

    if (document.referrer !== '') {
      // Reset state only if referrer is not from Dashboard Settings delete page
      const deletePagePathnames = ['/dashboard/settings/library_delete', '/dashboard/settings/delete']
      const referrer = new URL(document.referrer)
      const isReferredFromDeletePage = deletePagePathnames.includes(referrer.pathname)
      if (isReferredFromDeletePage) return
    }

    this.isPasswordShown = false
    this.prefilledPasswordByBrowser = ''
    this.setIsUsernameVerified(false)
    this.setPassword('')
    this.setPasswordValidationMessage('')
  }

  async submit(): Promise<void> {
    if (this.xUsernameVerificationScreen) {
      await this.continueWithEmailOrUsername()

      // Browser refills username and password input after clicking the Continue button
      // so we need to store the password of the account that the user selected
      if (this.password) {
        this.prefilledPasswordByBrowser = this.password
      }

      if (this.isUsernameVerified && !window.history.state?.didPushState) {
        // Enable the browser back button on login page
        // didPushState: true to prevent pushing redundant states
        window.history.pushState({ didPushState: true }, '', window.location.href)
      }

      window.addEventListener('popstate', this.goBackEmailVerificationScreen)
    } else {
      this.loginWithPassword()
    }
  }
}
